import { UseComboboxPropGetters } from '@synoptic/ui-kit/multi-select-combobox/multi-select-combobox.js';
import { Spinner } from '@synoptic/ui-kit/spinner/spinner.js';
import { PropsWithChildren, ReactNode, useEffect, useMemo } from 'react';
import { CaretArrowDownIcon } from '../icons/react/caret-arrow-down.tsx';
import { LinkButton } from '../link-button/link-button.tsx';
import {
  emptyDropdownPlaceholder,
  loadingContainer,
  multiSelectMenu,
  multiSelectMenuContainer,
  multiSelectMenuGroupTitle,
  multiSelectMenuItem,
  multiSelectMenuItemDisabled,
} from './multi-select-group.css.ts';

export const MultiSelectMenuContainer = ({ children }: PropsWithChildren) => (
  <div className={multiSelectMenuContainer}>{children}</div>
);

export function MultiSelectGroup<T extends { id: string }>({
  groupName,
  showLabel,
  items,
  selectedItems,
  getItemProps,
  loadMore,
  noMore,
  isItemDisabled,
  isInitialLoading,
  isLoadingMore,
  emptyPlaceholder,
  children,
}: {
  groupName?: string;
  showLabel?: boolean;
  items: T[];
  selectedItems: T[];
  getItemProps: UseComboboxPropGetters<T>['getItemProps'];
  loadMore: () => void;
  noMore: boolean;
  isItemDisabled?: (item: T) => boolean;
  isInitialLoading: boolean;
  isLoadingMore: boolean;
  emptyPlaceholder?: string;
  children: (item: T) => ReactNode;
}) {
  const itemsToRender = useMemo(() => {
    return (
      items?.filter((item) => !selectedItems.find((v) => v.id === item.id)) ||
      []
    );
  }, [selectedItems, items]);

  useEffect(() => {
    if (itemsToRender.length === 0 && !noMore) {
      loadMore();
    }
  }, [itemsToRender, loadMore, noMore]);

  return (
    <div>
      {showLabel && (
        <span className={multiSelectMenuGroupTitle}>{groupName}</span>
      )}
      {isInitialLoading && (
        <div className={loadingContainer}>
          <Spinner />
        </div>
      )}
      {itemsToRender.length > 0 && !isInitialLoading && (
        <ul className={multiSelectMenu}>
          {itemsToRender.map((item) => (
            <li
              className={
                isItemDisabled?.(item)
                  ? multiSelectMenuItemDisabled
                  : multiSelectMenuItem
              }
              key={item.id}
              {...getItemProps({
                item: item,
                id: item.id,
              })}
            >
              {children(item)}
            </li>
          ))}
        </ul>
      )}
      {itemsToRender.length === 0 && !isInitialLoading && !isLoadingMore && (
        <p className={emptyDropdownPlaceholder}>
          {emptyPlaceholder || 'No items found'}
        </p>
      )}
      {!noMore && !isInitialLoading && (
        <div className={loadingContainer}>
          <LinkButton
            startIcon={<CaretArrowDownIcon width={16} height={16} />}
            size="medium"
            onClick={loadMore}
            endIcon={isLoadingMore && <Spinner />}
            disabled={isLoadingMore}
          >
            Show more
          </LinkButton>
        </div>
      )}
    </div>
  );
}
